import { useTranslation } from 'react-i18next';

import { Input } from '@/components';
import { cn } from '@/lib/utils';

interface BoxModelEditorProps {
  paddingTop?: number;
  paddingRight?: number;
  paddingBottom?: number;
  paddingLeft?: number;
  marginTop?: number;
  marginRight?: number;
  marginBottom?: number;
  marginLeft?: number;
  onPaddingChange: (side: 'top' | 'right' | 'bottom' | 'left', value: number | undefined) => void;
  onMarginChange: (side: 'top' | 'right' | 'bottom' | 'left', value: number | undefined) => void;
}

function SideInput({
  value,
  onChange,
  className = '',
}: {
  value: number | undefined;
  onChange: (v: number | undefined) => void;
  className?: string;
}) {
  return (
    <Input
      type="number"
      value={value ?? ''}
      onChange={(e) => {
        const v = e.target.value;
        onChange(v === '' ? undefined : parseInt(v));
      }}
      containerClassName="w-10"
      className={cn(
        'h-5 w-10 rounded-none border-transparent bg-transparent px-0 py-0 text-center font-mono text-[10px]',
        'focus:ring-0 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
        className,
      )}
      min={0}
      max={200}
    />
  );
}

export function BoxModelEditor({
  paddingTop,
  paddingRight,
  paddingBottom,
  paddingLeft,
  marginTop,
  marginRight,
  marginBottom,
  marginLeft,
  onPaddingChange,
  onMarginChange,
}: BoxModelEditorProps) {
  const { t } = useTranslation();
  return (
    <div className="space-y-1">
      {/* Visual Box Model */}
      <div className="relative mx-auto w-full max-w-[240px]">
        {/* Margin layer */}
        <div className="bg-orange-50 dark:bg-orange-900/20 border border-dashed border-orange-300 dark:border-orange-700 rounded-lg p-1">
          <div className="text-[9px] text-orange-500 dark:text-orange-400 text-center uppercase tracking-wider mb-0.5">
            {t('tenant.systemModuleBuilder.style.spacing.margin')}
          </div>

          {/* Margin Top */}
          <div className="flex justify-center">
            <SideInput
              value={marginTop}
              onChange={(v) => onMarginChange('top', v)}
              className="text-orange-600 dark:text-orange-400"
            />
          </div>

          <div className="flex items-center">
            {/* Margin Left */}
            <SideInput
              value={marginLeft}
              onChange={(v) => onMarginChange('left', v)}
              className="text-orange-600 dark:text-orange-400"
            />

            {/* Padding layer */}
            <div className="flex-1 bg-accent-50 dark:bg-accent-900/20 border border-dashed border-accent-300 dark:border-accent-700 rounded p-1 mx-1">
              <div className="text-[9px] text-accent-500 dark:text-accent-400 text-center uppercase tracking-wider mb-0.5">
                {t('tenant.systemModuleBuilder.style.spacing.padding')}
              </div>

              {/* Padding Top */}
              <div className="flex justify-center">
                <SideInput
                  value={paddingTop}
                  onChange={(v) => onPaddingChange('top', v)}
                  className="text-accent-600 dark:text-accent-400"
                />
              </div>

              <div className="flex items-center justify-between">
                {/* Padding Left */}
                <SideInput
                  value={paddingLeft}
                  onChange={(v) => onPaddingChange('left', v)}
                  className="text-accent-600 dark:text-accent-400"
                />

                {/* Content */}
                <div className="w-12 h-6 bg-accent-100 dark:bg-accent-900/30 rounded flex items-center justify-center">
                  <span className="text-[9px] text-accent-500">
                    {t('tenant.systemModuleBuilder.style.spacing.content')}
                  </span>
                </div>

                {/* Padding Right */}
                <SideInput
                  value={paddingRight}
                  onChange={(v) => onPaddingChange('right', v)}
                  className="text-accent-600 dark:text-accent-400"
                />
              </div>

              {/* Padding Bottom */}
              <div className="flex justify-center">
                <SideInput
                  value={paddingBottom}
                  onChange={(v) => onPaddingChange('bottom', v)}
                  className="text-accent-600 dark:text-accent-400"
                />
              </div>
            </div>

            {/* Margin Right */}
            <SideInput
              value={marginRight}
              onChange={(v) => onMarginChange('right', v)}
              className="text-orange-600 dark:text-orange-400"
            />
          </div>

          {/* Margin Bottom */}
          <div className="flex justify-center">
            <SideInput
              value={marginBottom}
              onChange={(v) => onMarginChange('bottom', v)}
              className="text-orange-600 dark:text-orange-400"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
